import { useState } from "react";
import { Check, Pin, PinOff } from "lucide-react";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import {
  supplierListQuery,
  type SupplierListItem,
} from "@/app/Suppliers/GraphQL/supplierListQuery";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { SupplierIcon } from "@/app/Suppliers/Components/SupplierIcon";
import { usePinnedSuppliers } from "@/app/Suppliers/Utils/usePinnedSuppliers";
import { cn } from "@/lib/utils";

/**
 * Standalone (non-Formik) searchable supplier picker with per-supplier
 * pin/unpin, for widgets that sync a supplier id to something other than a
 * form field (e.g. a URL search param).
 */
export function SupplierCombobox({
  value,
  onChange,
  renderTrigger,
  archived = false,
  className,
}: {
  value?: string;
  onChange: (id: string) => void;
  renderTrigger: (open: boolean) => React.ReactNode;
  archived?: boolean | null;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const { pinnedIds, isPinned, togglePin } = usePinnedSuppliers();

  const { data } = useGqlQuery(supplierListQuery, {
    first: 200,
    page: 1,
    archived,
    search: search || undefined,
  });
  const supplierList = data?.suppliers.data ?? [];
  const unpinned = supplierList.filter((s) => !pinnedIds.includes(s.id));

  // Pinned suppliers are fetched by id directly so they always show, even
  // when they don't match the current search or fall outside the first page.
  const { data: pinnedData } = useGqlQuery(
    supplierListQuery,
    {
      first: pinnedIds.length,
      page: 1,
      archived: null,
      ids: pinnedIds,
    },
    { enabled: pinnedIds.length > 0 },
  );
  const pinned = pinnedIds.length > 0 ? (pinnedData?.suppliers.data ?? []) : [];

  function renderItem(s: SupplierListItem) {
    return (
      <CommandItem
        key={s.id}
        value={s.name}
        onSelect={() => {
          onChange(s.id);
          setOpen(false);
        }}
        className="flex items-center gap-2"
      >
        <SupplierIcon model={s} className="size-5 rounded" />
        <span className="flex-1 truncate">{s.name}</span>
        {s.id === value ? (
          <Check className="size-4 shrink-0 text-indigo-600" />
        ) : null}
        <button
          type="button"
          aria-label={isPinned(s.id) ? "Unpin supplier" : "Pin supplier"}
          onClick={(e) => {
            e.stopPropagation();
            togglePin(s.id);
          }}
          onPointerDown={(e) => e.stopPropagation()}
          className="shrink-0 text-gray-400 transition-colors hover:text-gray-900"
        >
          {isPinned(s.id) ? (
            <PinOff className="size-4" />
          ) : (
            <Pin className="size-4" />
          )}
        </button>
      </CommandItem>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{renderTrigger(open)}</PopoverTrigger>
      <PopoverContent className={cn("w-64 p-0", className)} align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search suppliers..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>No suppliers found.</CommandEmpty>
            {pinned.length > 0 ? (
              <CommandGroup heading="Pinned">
                {pinned.map(renderItem)}
              </CommandGroup>
            ) : null}
            <CommandGroup
              heading={pinned.length > 0 ? "All suppliers" : undefined}
            >
              {unpinned.map(renderItem)}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
